import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import api from '@/lib/api';

/**
 * Shared alumno self-registration form (used by /register and /invitar/:token).
 * Requires a valid gym invite code; submits to /public/register-by-invite.
 */
export default function AlumnoRegisterForm({ token, gymName, onSuccess }) {
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        dni: '',
        phone: '',
        email: '',
        birth_date: '',
        password: '',
        password_confirmation: '',
    });

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            await api.post('/public/register-by-invite', { ...form, token });
            setSuccess(true);
            onSuccess?.();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al registrarse.');
        } finally {
            setSaving(false);
        }
    };

    if (success) {
        return (
            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader className="text-center">
                    <div className="flex justify-center mb-4">
                        <div className="h-12 w-12 bg-white rounded-xl flex items-center justify-center">
                            <CheckCircle2 className="h-6 w-6 text-black" />
                        </div>
                    </div>
                    <CardTitle className="text-2xl font-bold text-white">Registro exitoso</CardTitle>
                    <CardDescription className="text-white/60">
                        {gymName ? `Ya sos parte de ${gymName}.` : 'Tu cuenta fue creada correctamente.'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                    <p className="text-sm text-white/60">
                        Ya podés iniciar sesión con tu DNI o email para acceder a tu cuenta y consultar tu tarjeta de membresía.
                    </p>
                    <Button onClick={() => navigate('/login')} className="w-full">
                        Iniciar sesión
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                        Volver al inicio
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="bg-[#0a0a0a] border-hairline-dark">
            <CardHeader>
                <CardTitle className="uppercase tracking-wide text-white">Registrate como alumno</CardTitle>
                <CardDescription className="text-white/60">Completá tus datos para unirte al gimnasio.</CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="bg-[#0a0a0a] text-white text-sm p-3 rounded-md flex items-start gap-2 border border-white/20">
                            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                            {error}
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="first_name" className="text-white">Nombre</Label>
                            <Input id="first_name" name="first_name" value={form.first_name} onChange={handleChange} required className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="last_name" className="text-white">Apellido</Label>
                            <Input id="last_name" name="last_name" value={form.last_name} onChange={handleChange} required className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="dni" className="text-white">DNI</Label>
                        <Input id="dni" name="dni" value={form.dni} onChange={handleChange} required className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="phone" className="text-white">Teléfono</Label>
                            <Input id="phone" name="phone" value={form.phone} onChange={handleChange} className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-white">Email</Label>
                            <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="birth_date" className="text-white">Fecha de nacimiento</Label>
                        <Input id="birth_date" name="birth_date" type="date" value={form.birth_date} onChange={handleChange} className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-white">Contraseña</Label>
                            <Input id="password" name="password" type="password" placeholder="Mínimo 8 caracteres" value={form.password} onChange={handleChange} minLength={8} required className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password_confirmation" className="text-white">Confirmar contraseña</Label>
                            <Input id="password_confirmation" name="password_confirmation" type="password" placeholder="Repetí la contraseña" value={form.password_confirmation} onChange={handleChange} minLength={8} required className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark" />
                        </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={saving}>
                        {saving ? 'Registrando...' : 'Registrarme'}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
}
